// TipTap custom node extensions for inline and display math.
// Each node renders KaTeX when blurred and a live MathLive field when focused.

import { Node, mergeAttributes, InputRule } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import type { NodeViewProps } from "@tiptap/react";
import { useState, useRef, useEffect, useCallback } from "react";
import katex from "katex";
import { MathEditor } from "../components/MathEditor";

// ── LaTeX preprocessing for KaTeX compatibility ─────────────────────────────

/**
 * Convert TeX primitives and multi-line constructs into forms KaTeX understands.
 * - \eqalign{...} → \begin{aligned}...\end{aligned}
 * - \cr → \\
 * - \over → \frac  (simple cases)
 * - bare \\ or & at top level → auto-wrap in aligned
 */
export function preprocessForKatex(raw: string): string {
    let tex = raw;

    // Strip \label{...} — KaTeX doesn't support it and renders it as literal text
    tex = tex.replace(/\\label\s*\{[^}]*\}/g, "");

    // AMS wide math accents → KaTeX equivalents (amsmath defines these; KaTeX does not)
    tex = tex.replace(/\\Tilde\s*\{/g, "\\widetilde{");
    tex = tex.replace(/\\Hat\s*\{/g, "\\widehat{");
    tex = tex.replace(/\\Bar\s*\{/g, "\\overline{");
    tex = tex.replace(/\\Vec\s*\{/g, "\\overrightarrow{");
    tex = tex.replace(/\\Dot\s*\{/g, "\\dot{");
    tex = tex.replace(/\\Ddot\s*\{/g, "\\ddot{");
    tex = tex.replace(/\\Check\s*\{/g, "\\check{");
    tex = tex.replace(/\\Breve\s*\{/g, "\\breve{");
    tex = tex.replace(/\\Acute\s*\{/g, "\\acute{");
    tex = tex.replace(/\\Grave\s*\{/g, "\\grave{");

    // \eqalign{...} → \begin{aligned}...\end{aligned}
    tex = tex.replace(/\\eqalign\s*\{/g, "\\begin{aligned}");
    // Close matching brace — find the balanced closing }
    // We only do a simple approach: replace last } of the \eqalign block
    tex = replaceEqalignClose(tex);

    // \cr → \\
    tex = tex.replace(/\\cr\b/g, "\\\\");

    // \displaylines{...} → \begin{gathered}...\end{gathered}
    tex = tex.replace(/\\displaylines\s*\{/g, "\\begin{gathered}");
    tex = replaceEnvClose(tex, "gathered");

    // \pmatrix{...} → \begin{pmatrix}...\end{pmatrix} (if not already)
    tex = tex.replace(/\\pmatrix\s*\{/g, "\\begin{pmatrix}");
    tex = replaceEnvClose(tex, "pmatrix");

    // If the expression contains \\ or & but is NOT already inside a known
    // multi-line environment, wrap it in \begin{aligned}...\end{aligned}
    const knownEnvs = /\\begin\{(aligned|align|alignedat|gathered|gather|split|cases|array|matrix|[pbBvV]matrix|eqnarray|multline|subarray)/;
    if (!knownEnvs.test(tex) && (/\\\\/.test(tex) || /(?<!\\)&/.test(tex))) {
        tex = `\\begin{aligned}${tex}\\end{aligned}`;
    }

    return tex;
}

/** Replace the balanced closing } that closes a \begin{aligned} we just injected */
function replaceEqalignClose(tex: string): string {
    return replaceEnvClose(tex, "aligned");
}

function replaceEnvClose(tex: string, envName: string): string {
    const marker = `\\begin{${envName}}`;
    let idx = tex.indexOf(marker);
    while (idx !== -1) {
        const contentStart = idx + marker.length;
        // Find balanced closing }
        let depth = 1;
        let i = contentStart;
        while (i < tex.length && depth > 0) {
            if (tex[i] === "{" && tex[i - 1] !== "\\") depth++;
            else if (tex[i] === "}" && tex[i - 1] !== "\\") depth--;
            if (depth > 0) i++;
        }
        if (depth === 0) {
            tex = tex.slice(0, i) + `\\end{${envName}}` + tex.slice(i + 1);
        }
        idx = tex.indexOf(marker, i + 1);
    }
    return tex;
}

// ── Safe KaTeX rendering ─────────────────────────────────────────────────────

function katexRender(latex: string, displayMode: boolean): string {
    if (!latex.trim()) return "";
    const processed = preprocessForKatex(latex);
    try {
        return katex.renderToString(processed, {
            displayMode,
            throwOnError: false,
            strict: "ignore",
            trust: true,
            macros: {
                "\\Re": "\\operatorname{Re}",

            },
        });
    } catch {
        return `<span class="math-error">${latex}</span>`;
    }
}

// ── Auto-scaling wrapper: shrinks KaTeX output to fit container ───────────────

function AutoScaleMath({ html, displayMode }: { html: string; displayMode: boolean }) {
    const containerRef = useRef<HTMLSpanElement>(null);
    const innerRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        const container = containerRef.current;
        const inner = innerRef.current;
        if (!container || !inner) return;

        // Reset any previous scaling
        inner.style.transform = "";
        inner.style.transformOrigin = "center center";

        // Measure after KaTeX renders
        requestAnimationFrame(() => {
            const parentWidth = container.parentElement?.clientWidth ?? container.clientWidth;
            const naturalWidth = inner.scrollWidth;
            if (naturalWidth > parentWidth && parentWidth > 0) {
                const scale = parentWidth / naturalWidth;
                inner.style.transform = `scale(${Math.max(scale, 0.4)})`;
                inner.style.transformOrigin = displayMode ? "center center" : "left center";
                // Adjust container height to match scaled content
                container.style.height = `${inner.scrollHeight * Math.max(scale, 0.4)}px`;
            } else {
                container.style.height = "";
            }
        });
    }, [html, displayMode]);

    return (
        <span ref={containerRef} style={{ display: displayMode ? "block" : "inline-block", maxWidth: "100%", overflow: "hidden" }}>
            <span ref={innerRef} style={{ display: "inline-block", whiteSpace: "nowrap" }}
                dangerouslySetInnerHTML={{ __html: html }} />
        </span>
    );
}

// ── Hook: exit editing when user clicks outside the node wrapper ──────────────

function useClickOutsideToExit(
    wrapperRef: React.RefObject<HTMLElement | null>,
    editing: boolean,
    onExit: () => void
) {
    useEffect(() => {
        if (!editing) return;
        const handler = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.closest('math-field, .ML__keyboard, .ML__popover, .ML__menu, [part="virtual-keyboard"]')) {
                return;
            }
            if (wrapperRef.current && !wrapperRef.current.contains(target as unknown as globalThis.Node)) {
                onExit();
            }
        };
        // Use mousedown so it fires before focus shifts
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [editing, onExit, wrapperRef]);
}

// ── Inline Math NodeView ─────────────────────────────────────────────────────

function InlineMathNodeView({ node, updateAttributes, editor, getPos }: NodeViewProps) {
    const latex: string = node.attrs.latex ?? "";
    const instanceId: string = node.attrs.instanceId ?? "";
    // Freshly inserted nodes (empty latex) open immediately in edit mode
    const [editing, setEditing] = useState(() => latex === "");
    const wrapperRef = useRef<HTMLSpanElement>(null);
    const latexRef = useRef(latex);
    latexRef.current = latex;

    // Fix alternating mm bug: TipTap sometimes reuses the NodeView component
    const prevInstanceId = useRef(instanceId);
    useEffect(() => {
        if (instanceId !== prevInstanceId.current) {
            prevInstanceId.current = instanceId;
            if (latex === "") setEditing(true);
        }
    }, [instanceId, latex]);

    const exitEdit = useCallback(() => {
        const currentLatex = latexRef.current;

        if (!currentLatex || currentLatex.trim() === "") {
            const pos = typeof getPos === 'function' ? getPos() : undefined;
            if (pos !== undefined) {
                editor.commands.deleteRange({ from: pos, to: pos + node.nodeSize });
                editor.commands.focus();
                return;
            }
        }

        setEditing(false);

        // Use setTimeout(0) rather than rAF: the callback runs after React commits
        // the state update (which fires as a microtask) so editor commands don't
        // collide with an in-progress React render batch.  The 0 ms delay is still
        // orders of magnitude smaller than any replay event gap (≥ 400 ms).
        const snapPos = typeof getPos === 'function' ? getPos() : undefined;
        setTimeout(() => {
            if (snapPos == null) return;
            editor.view.focus();
            editor.commands.setTextSelection(snapPos + node.nodeSize);
        }, 0);
    }, [editor, getPos, node.nodeSize]);

    useClickOutsideToExit(wrapperRef, editing, exitEdit);

    return (
        <NodeViewWrapper as="span" data-type="inline-math" className="math-node-inline">
            <span ref={wrapperRef}>
                {editing ? (
                    <span className="math-live-inline">
                        <MathEditor
                            key={instanceId}
                            initialLatex={latex}
                            autoFocus
                            onLatexChange={(l) => updateAttributes({ latex: l })}
                            onEscape={exitEdit}
                        />
                    </span>
                ) : (
                    <span
                        className="math-rendered math-rendered--inline"
                        onMouseDown={(e) => { e.preventDefault(); setEditing(true); }}
                        title="Click to edit"
                    >
                        {latex ? (
                            <AutoScaleMath html={katexRender(latex, false)} displayMode={false} />
                        ) : (
                            <span className="math-empty-chip">math</span>
                        )}
                    </span>
                )}
            </span>
        </NodeViewWrapper>
    );
}

// ── Display Math NodeView ────────────────────────────────────────────────────

function DisplayMathNodeView({ node, updateAttributes, editor, getPos }: NodeViewProps) {
    const latex: string = node.attrs.latex ?? "";
    const [editing, setEditing] = useState(() => latex === "");
    const wrapperRef = useRef<HTMLDivElement>(null);
    // Track current latex in a ref so exitEdit always sees the latest value
    const latexRef = useRef(latex);
    latexRef.current = latex;

    const wrappedUpdateAttributes = useCallback((attrs: { latex: string }) => {
        updateAttributes(attrs);
    }, [updateAttributes]);

    const exitEdit = useCallback(() => {
        const currentLatex = latexRef.current;
        if (!currentLatex || currentLatex.trim() === "") {
            const pos = typeof getPos === 'function' ? getPos() : undefined;
            if (pos !== undefined) {
                editor.commands.deleteRange({ from: pos, to: pos + node.nodeSize });
                editor.commands.focus();
                return;
            }
        }
        setEditing(false);

        const snapPos = typeof getPos === 'function' ? getPos() : undefined;
        setTimeout(() => {
            if (snapPos == null) return;
            editor.view.focus();
            const afterMath = snapPos + node.nodeSize;
            const $after = editor.state.doc.resolve(afterMath);
            if (
                $after.parentOffset === $after.parent.content.size &&
                $after.parent.type.name !== "doc"
            ) {
                editor.chain()
                    .insertContentAt(afterMath, { type: "paragraph" })
                    .setTextSelection(afterMath + 1)
                    .run();
            } else {
                editor.commands.setTextSelection(afterMath);
            }
        }, 0);
    }, [editor, getPos, node.nodeSize]);

    useClickOutsideToExit(wrapperRef, editing, exitEdit);

    return (
        <NodeViewWrapper data-type="display-math" className="math-node-display">
            <div ref={wrapperRef}>
                {editing ? (
                    <div className="math-live-display">
                        <MathEditor
                            initialLatex={latex}
                            autoFocus
                            onLatexChange={(l) => wrappedUpdateAttributes({ latex: l })}
                            onEscape={exitEdit}
                        />
                    </div>
                ) : (
                    <div
                        className="math-rendered math-rendered--display"
                        onMouseDown={(e) => { e.preventDefault(); setEditing(true); }}
                        title="Click to edit"
                    >
                        {latex ? (
                            <AutoScaleMath html={katexRender(latex, true)} displayMode={true} />
                        ) : (
                            <div className="math-empty-chip math-empty-chip--display">
                                display math — click to edit
                            </div>
                        )}
                    </div>
                )}
            </div>
        </NodeViewWrapper>
    );
}

// ── InlineMath TipTap Extension ──────────────────────────────────────────────

export const InlineMath = Node.create({
    name: "inlineMath",
    group: "inline",
    inline: true,
    atom: true,

    addAttributes() {
        return {
            latex: { default: "" },
            // Unique id stamped on each insertion — used as React key to force
            // MathEditor to fully remount on every new node, even if TipTap
            // reuses the NodeView component instance (calls update() instead of
            // destroy+create). Without this, autoFocus never re-fires on even attempts.
            instanceId: { default: "" },
        };
    },

    parseHTML() {
        return [{ tag: 'span[data-type="inline-math"]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ["span", mergeAttributes(HTMLAttributes, { "data-type": "inline-math" })];
    },

    addNodeView() {
        return ReactNodeViewRenderer(InlineMathNodeView, {
            // Stop ProseMirror from intercepting events that target the math content,
            // but let clicks on empty wrapper space pass through for cursor placement.
            stopEvent: ({ event }) => {
                if (event.type === 'mousedown') {
                    const target = event.target as HTMLElement;
                    return !!target.closest('.math-rendered, .math-live-inline, .math-live-display, math-field');
                }
                if (event.type === 'keydown' || event.type === 'keyup' || event.type === 'keypress') {
                    const target = event.target as HTMLElement;
                    if (target.closest('math-field')) {
                        return true;
                    }
                }
                return false;
            },
        });
    },

    // Typing "mm" at word boundary inserts an inline math node with spacing
    addInputRules() {
        return [
            new InputRule({
                find: /(?:^|[\s.,;!?()\[\]{}'"\-])mm$/,
                handler: ({ state, range }) => {
                    const { tr, schema } = state;
                    const from = range.from;
                    const to = range.to;
                    // Only delete the 2 "mm" chars, not any preceding boundary char
                    const matchedText = state.doc.textBetween(from, to);
                    const mmStart = from + matchedText.length - 2;
                    const id = String(Date.now());

                    // Create the math node
                    const mathNode = this.type.create({ latex: "", instanceId: id });

                    // Check if we need spacing before (not at start of paragraph)
                    const $pos = state.doc.resolve(mmStart);
                    const isAtStart = $pos.parentOffset === 0;

                    // Build content: optional leading space + math node + trailing space
                    const textType = schema.nodes.text;
                    const spacing = "  "; // Two spaces for visual separation

                    if (isAtStart) {
                        // At start of paragraph: just math node + trailing space
                        const trailingSpace = schema.text(spacing);
                        tr.replaceWith(mmStart, to, [mathNode, trailingSpace]);
                    } else {
                        // Mid-paragraph: leading space + math node + trailing space
                        const leadingSpace = schema.text(spacing);
                        const trailingSpace = schema.text(spacing);
                        tr.replaceWith(mmStart, to, [leadingSpace, mathNode, trailingSpace]);
                    }
                },
            }),
        ];
    },
});

// ── DisplayMath TipTap Extension ─────────────────────────────────────────────

export const DisplayMath = Node.create({
    name: "displayMath",
    group: "block",
    atom: true,

    addAttributes() {
        return { latex: { default: "" } };
    },

    parseHTML() {
        return [{ tag: 'div[data-type="display-math"]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ["div", mergeAttributes(HTMLAttributes, { "data-type": "display-math" })];
    },

    addNodeView() {
        return ReactNodeViewRenderer(DisplayMathNodeView, {
            // Stop ProseMirror from intercepting events that target the math content,
            // but let clicks on empty wrapper space pass through for cursor placement.
            stopEvent: ({ event }) => {
                if (event.type === 'mousedown') {
                    const target = event.target as HTMLElement;
                    return !!target.closest('.math-rendered, .math-live-inline, .math-live-display, math-field');
                }
                if (event.type === 'keydown' || event.type === 'keyup' || event.type === 'keypress') {
                    const target = event.target as HTMLElement;
                    if (target.closest('math-field')) {
                        return true;
                    }
                }
                return false;
            },
        });
    },

    // Typing "dm" at the start of a line inserts a display math block
    addInputRules() {
        return [
            new InputRule({
                find: /^dm$/,
                handler: ({ state, range }) => {
                    const { tr } = state;
                    const node = this.type.create({ latex: "" });
                    // Replace the whole paragraph that contains "dm"
                    const $from = state.doc.resolve(range.from);
                    const start = $from.start($from.depth) - 1; // paragraph start
                    const end = $from.end($from.depth) + 1;     // paragraph end
                    tr.replaceWith(start, end, node);
                },
            }),
        ];
    },
});

// ── Numbered Equation NodeView ───────────────────────────────────────────────

function NumberedEquationNodeView({ node, updateAttributes, editor, getPos }: NodeViewProps) {
    const latex: string = node.attrs.latex ?? "";
    const [editing, setEditing] = useState(() => latex === "");
    const wrapperRef = useRef<HTMLDivElement>(null);
    const latexRef = useRef(latex);
    latexRef.current = latex;

    const exitEdit = useCallback(() => {
        const pos = typeof getPos === "function" ? getPos() : undefined;
        const nodeSize = node.nodeSize;
        const currentLatex = latexRef.current;

        if (!currentLatex || currentLatex.trim() === "") {
            if (pos !== undefined) {
                editor.commands.deleteRange({ from: pos, to: pos + nodeSize });
                editor.commands.focus();
                return;
            }
        }

        setEditing(false);

        if (pos !== undefined) {
            setTimeout(() => {
                editor.view.focus();
                const afterMath = pos + nodeSize;
                const $after = editor.state.doc.resolve(afterMath);
                if (
                    $after.parentOffset === $after.parent.content.size &&
                    $after.parent.type.name !== "doc"
                ) {
                    editor.chain()
                        .insertContentAt(afterMath, { type: "paragraph" })
                        .setTextSelection(afterMath + 1)
                        .run();
                } else {
                    editor.commands.setTextSelection(afterMath);
                }
            }, 0);
        }
    }, [editor, getPos, node.nodeSize]);

    useClickOutsideToExit(wrapperRef, editing, exitEdit);

    return (
        <NodeViewWrapper
            data-type="numbered-equation"
            className={`math-node-equation${editing ? " math-node-equation--editing" : ""}`}
        >
            <div ref={wrapperRef}>
                {editing ? (
                    <div className="math-live-display">
                        <MathEditor
                            initialLatex={latex}
                            autoFocus
                            onLatexChange={(l) => updateAttributes({ latex: l })}
                            onEscape={exitEdit}
                        />
                    </div>
                ) : (
                    <div
                        className="math-rendered math-rendered--display"
                        onMouseDown={(e) => { e.preventDefault(); setEditing(true); }}
                        title="Click to edit"
                    >
                        {latex ? (
                            <AutoScaleMath html={katexRender(latex, true)} displayMode={true} />
                        ) : (
                            <div className="math-empty-chip math-empty-chip--display">
                                numbered equation — click to edit
                            </div>
                        )}
                    </div>
                )}
            </div>
        </NodeViewWrapper>
    );
}

// ── NumberedEquation TipTap Extension ────────────────────────────────────────

export const NumberedEquation = Node.create({
    name: "numberedEquation",
    group: "block",
    atom: true,

    addAttributes() {
        return { latex: { default: "" } };
    },

    parseHTML() {
        return [{ tag: 'div[data-type="numbered-equation"]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ["div", mergeAttributes(HTMLAttributes, { "data-type": "numbered-equation" })];
    },

    addNodeView() {
        return ReactNodeViewRenderer(NumberedEquationNodeView, {
            // Stop ProseMirror from intercepting events that target the math content,
            // but let clicks on empty wrapper space pass through for cursor placement.
            stopEvent: ({ event }) => {
                if (event.type === "mousedown") {
                    const target = event.target as HTMLElement;
                    return !!target.closest(".math-rendered, .math-live-inline, .math-live-display, math-field");
                }
                if (event.type === "keydown" || event.type === "keyup" || event.type === "keypress") {
                    const target = event.target as HTMLElement;
                    if (target.closest("math-field")) return true;
                }
                return false;
            },
        });
    },

    // Typing "nn" at the start of a line inserts a numbered equation block
    addInputRules() {
        return [
            new InputRule({
                find: /^nn$/,
                handler: ({ state, range }) => {
                    const { tr } = state;
                    const node = this.type.create({ latex: "" });
                    const $from = state.doc.resolve(range.from);
                    const start = $from.start($from.depth) - 1;
                    const end = $from.end($from.depth) + 1;
                    tr.replaceWith(start, end, node);
                },
            }),
        ];
    },
});
